import { useApiQuery, type Column } from 'grabbitbot-legacy-ui'
import { Table } from '../../components/DataTable'
import { paymentReads } from '../../api/fee'
import { PageTitle, QueryState } from '../../components/common'
import { useProject } from '../../layout/projectContext'
import { formatDateTime, minorToMajor } from '../../lib/format'

/** A raw Stripe invoice as relayed by `GET …/payment/invoices` (a Stripe
 * search result `{data: [...]}`); `total` is in MINOR units here. */
interface StripeInvoice {
  id: string
  number: string | null
  total: number
  currency: string
  effective_at?: number | null
  created?: number | null
  hosted_invoice_url?: string | null
  invoice_pdf?: string | null
}

/** Port of `PaymentInvoiceListComponent` (`projects/:projectId/payment/invoices`):
 * the caller's Stripe receipts, with links out to Stripe. */
export function PaymentInvoicesPage() {
  const { projectId } = useProject()
  const q = useApiQuery(() => paymentReads.invoices(projectId), [projectId])
  const raw = q.data as unknown
  const rows: StripeInvoice[] = Array.isArray(raw)
    ? (raw as StripeInvoice[])
    : raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)
      ? ((raw as { data: StripeInvoice[] }).data)
      : []
  const stripeError = typeof raw === 'string' ? raw : null
  const columns: Column<StripeInvoice>[] = [
    { key: 'number', label: 'Number' },
    {
      key: 'date',
      label: 'Date',
      sortValue: (i) => i.effective_at ?? i.created ?? 0,
      format: (_v, i) => formatDateTime(i.effective_at ?? i.created ?? null),
    },
    {
      key: 'total',
      label: 'Total',
      align: 'right',
      numeric: true,
      format: (_v, i) => `${String(i.currency ?? '').toUpperCase()} ${minorToMajor(Number(i.total) || 0)}`,
    },
    {
      key: 'links',
      label: '',
      sortable: false,
      format: (_v, i) => (
        <span className="portal-inline-actions">
          {i.hosted_invoice_url && (
            <a href={i.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
              View on Stripe
            </a>
          )}
          {i.invoice_pdf && (
            <a href={i.invoice_pdf} target="_blank" rel="noopener noreferrer">
              Download PDF from Stripe
            </a>
          )}
        </span>
      ),
    },
  ]
  return (
    <div className="portal-card">
      <PageTitle title="Invoices" />
      <QueryState loading={q.loading} error={q.error}>
        {stripeError && (
          <p role="alert" className="portal-notice portal-notice--error">
            Invoices could not be loaded from Stripe.
          </p>
        )}
        <div className="portal-table-scroll">
          <Table columns={columns} rows={rows} rowKey={(i) => i.id} initialSort={{ key: 'date', dir: 'desc' }} />
        </div>
        {!stripeError && rows.length === 0 && <p className="portal-text-muted">No invoices yet.</p>}
      </QueryState>
    </div>
  )
}
